import React, { useMemo, useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FrameworkKey } from '../../../../../utils/enums';

const FrameworkSelector = ({ frameworks, formData, errors, creating, setFormData, setErrors }) => {
  const theme = useTheme();
  const isSOC2 = formData.framework_base === FrameworkKey.SOC2;

  const baseFrameworks = useMemo(() => {
    const seen = new Map();
    frameworks.forEach(f => {
      const name = f.framework_name?.toUpperCase() ?? '';
      let entry;

      if (name.includes('SOC')) {
        entry = { key: FrameworkKey.SOC2, name: 'SOC 2' };
      } else {
        entry = {
          key: f.framework_id?.toString(),
          name: f.framework_name
        };
      }

      if (entry && !seen.has(entry.key)) {
        seen.set(entry.key, { ...f, ...entry });
      }
    });
    return Array.from(seen.values());
  }, [frameworks]);

  const selectedFramework = useMemo(() => {
    const id = formData.soc2_standard_id ?? formData.framework_id;
    return id
      ? frameworks.find(f => Number(f.framework_id) === Number(id))
      : null;
  }, [frameworks, formData.framework_id, formData.soc2_standard_id]);

  useEffect(() => {
    if (!selectedFramework) return;
    setFormData(prev => ({
      ...prev,
      minimum_window_months: selectedFramework.minimum_evidence_window_size ?? null,
      maximum_window_months: selectedFramework.maximum_evidence_window_size ?? null,
      framework_key: selectedFramework?.key,
    }));
  }, [selectedFramework, setFormData]);

  const clearErrors = (...keys) =>
    setErrors(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.includes(k))));

  const handleBaseChange = (value) => {
    setFormData(prev => ({
      ...prev,
      framework_base: value,
      framework_type: null,
      soc2_standard_id: null,
      framework_id: value === FrameworkKey.SOC2 ? null : parseInt(value),
      framework_key: null,
    }));
    clearErrors('framework_id', 'framework_type', 'soc2_standard_id');
  };

  const handleTypeChange = (value) => {
    const soc2Framework = frameworks.find(f => f.framework_name?.toUpperCase().includes('SOC'));
    setFormData(prev => ({
      ...prev,
      framework_type: value,
      soc2_standard_id: soc2Framework?.framework_id || null
    }));
    clearErrors('framework_type', 'soc2_standard_id');
  };

  const handleStandardChange = (id) => {
    setFormData(prev => ({ ...prev, soc2_standard_id: id }));
    clearErrors('soc2_standard_id');
  };

  const selectSx = {
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.customColors.border.strong,
    },
  };

  return (
    <>
      <Box sx={{ mb: 2.5 }}>
        <Typography
          component="label"
          sx={{
            display: "block",
            fontSize: "0.875rem",
            fontWeight: 600,
            color: theme.customColors?.text?.strong ?? "#374151",
            mb: 0.75,
          }}
        >
          Framework *
        </Typography>
        <FormControl fullWidth error={!!(errors.framework_base || errors.framework_id)} size="small">
          <Select
            value={formData.framework_base ?? ''}
            onChange={e => handleBaseChange(e.target.value)}
            disabled={creating}
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="" disabled>Select Framework</MenuItem>
            {baseFrameworks.map(f => (
              <MenuItem key={f.key} value={f.key}>{f.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(errors.framework_base || errors.framework_id) && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errors.framework_base || errors.framework_id}
          </Alert>
        )}
      </Box>

      {isSOC2 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: theme.customColors?.text?.strong ?? "#374151",
              mb: 0.75,
            }}
          >
            Type *
          </Typography>
          <FormControl fullWidth error={!!errors.framework_type} size="small">
            <Select
              value={formData.framework_type ?? ''}
              onChange={e => handleTypeChange(e.target.value)}
              disabled={creating}
              displayEmpty
              sx={selectSx}
            >
              <MenuItem value="" disabled>Select Type</MenuItem>
              <MenuItem value="type1">Type 1</MenuItem>
              <MenuItem value="type2">Type 2</MenuItem>
            </Select>
          </FormControl>
          {errors.framework_type && <Alert severity="error" sx={{ mt: 1 }}>{errors.framework_type}</Alert>}
        </Box>
      )}

    </>
  );
};

export default FrameworkSelector;